import React from 'react';
import { Box, TableBody, TableCell, TableRow, Checkbox, Tooltip } from '@mui/material';
import { tableRowTransparentSelectionSx } from '../../../../styles/theme/tableRowOverlays';
import {
  VisibilityOutlined as VisibilityIcon,
  ReceiptOutlined as ReceiptIcon,
  LockOutlined as LockIcon,
  LockOpenOutlined as LockOpenIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import { formatDate } from '../../../../utils/formatDate';
import {
  formatPieceSalePrice,
  pieceSalePriceTableCellSx,
} from '../../../../utils/formatters/money';
import { formatDecimal } from '../../../../utils/localeNumber';
import { getTransferDisplayAtIso } from '../../../../utils/inventory-transfers/transferDisplayDate';
import { calendarDaysSinceRegistration } from '../../../../utils/inventory/calendarDaysSinceTransfer';
import {
  InventoryItem,
  PaginationInfo,
  type WarehouseInventorySortField,
} from '../../../../types/inventory';
import { InventorySortableHeadCell } from '../InventorySortableHeadCell';
import { useInventoryQueryTable } from '../InventoryQueryTable.hooks';
import { mapWarehouseStatus, mapInventoryAvailability } from '../../../common/status';
import { usePermission } from '../../../../hooks/usePermission';
import { useInventoryWarehousePermissions } from '../../../../hooks/useInventoryWarehousePermissions';
import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
  isAvailabilityWrittenOff,
  isEligibleForWarehouseBlock,
  isSelectableForWarehouseTransfer,
} from '../../../../constants/inventory-status-labels';
import { ActionButton } from '../../../common/Table/Table.styles';
import EmptyStateMessage from '../../../common/EmptyStateMessage';
import {
  TableContainerStyled,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeadCell,
  StatusChip,
  ViewButton,
} from '../InventoryQueryTable.styles';

export interface WarehouseTableProps {
  items: InventoryItem[];
  onViewDetails: (id: string) => void;
  onEditItem?: (id: string) => void;
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  onTransferSelected?: (selectedItems: string[]) => void;
  selectedItems: string[];
  onSelectItems: (items: string[]) => void;
  hidePagination?: boolean;
  /** Impresión de etiqueta por unidad (107×11 mm). */
  onPrintLabel?: (item: InventoryItem) => void;
  onReserveItem?: (id: string) => void;
  /** Oculta Fecha Transfer. y Cód. Transferencia (p. ej. solo en Gestión en Almacén). */
  hideTransferColumns?: boolean;
  /** Oculta columna Sucursal (p. ej. solo en Gestión en Almacén). */
  hideLocationColumn?: boolean;
  sortBy: WarehouseInventorySortField;
  sortOrder: 'asc' | 'desc';
  onSortChange: (field: WarehouseInventorySortField) => void;
}

const WarehouseTable: React.FC<WarehouseTableProps> = ({
  items,
  onViewDetails,
  onEditItem,
  pagination,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPageChange,
  onItemsPerPageChange,
  selectedItems,
  onSelectItems,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hidePagination = false,
  onPrintLabel,
  onReserveItem,
  hideTransferColumns = false,
  hideLocationColumn = false,
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  const canView = usePermission('inventory.inventory-warehouse.view');
  const warehousePerms = useInventoryWarehousePermissions();

  const {
    selectableItems,
    isAllSelected,
    handleSelectItem,
    handleSelectAll,
    getTotalColumns,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getChipProps,
    getCheckboxStyles,
  } = useInventoryQueryTable({
    items,
    pagination,
    selectedItems,
    onSelectItems,
    onItemsPerPageChange,
    isInventoryBranchQuery: false,
    isAdmin: true,
    warehouseAttributeColumnCount: 5,
  });

  if (!canView) {
    return null;
  }

  const formatLengthCm = (value: number | undefined) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const cmValue = Number(value) / 10;
    return formatDecimal(cmValue, 2, { trimTrailingZeros: true }) || '—';
  };

  const displayText = (value: string | undefined | null) => {
    const t = value?.trim();
    return t ? t : '—';
  };

  const formatDaysInStock = (item: InventoryItem) => {
    const n = calendarDaysSinceRegistration(item);
    if (n === null) return '—';
    return String(n);
  };

  const emptyStateColSpan =
    getTotalColumns() - (hideTransferColumns ? 2 : 0) - (hideLocationColumn ? 1 : 0) + 1;

  return (
    <TableContainerStyled>
      <StyledTableContainer>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableHeadCell padding="checkbox">
                <Tooltip
                  title={
                    selectableItems.length === 0
                      ? 'No hay piezas seleccionables en esta página'
                      : isAllSelected
                        ? 'Deseleccionar todas las piezas de esta página'
                        : 'Seleccionar todas las piezas seleccionables de esta página'
                  }
                  placement="top"
                  arrow
                >
                  <Box>
                    <Checkbox
                      indeterminate={
                        selectedItems.length > 0 &&
                        !isAllSelected &&
                        selectableItems.some((item) => selectedItems.includes(item.id))
                      }
                      checked={isAllSelected && selectableItems.length > 0}
                      onChange={handleSelectAll}
                      size="small"
                      disabled={selectableItems.length === 0}
                      sx={getCheckboxStyles(selectableItems.length > 0)}
                    />
                  </Box>
                </Tooltip>
              </StyledTableHeadCell>
              <InventorySortableHeadCell
                label="Código"
                field="productCodeUnique"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              <InventorySortableHeadCell
                label="Nombre"
                field="productName"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              <InventorySortableHeadCell
                label="Tipo"
                field="categoryName"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              <InventorySortableHeadCell
                label="Fecha de registro"
                field="recordCompletedAt"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              <StyledTableHeadCell sx={{ minWidth: 108 }}>Días en inventario</StyledTableHeadCell>
              <InventorySortableHeadCell
                label="Nº Registro"
                field="recordCode"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              <InventorySortableHeadCell
                label="Estado Inventario"
                field="status"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              <StyledTableHeadCell>Talla</StyledTableHeadCell>
              <StyledTableHeadCell>Color</StyledTableHeadCell>
              <StyledTableHeadCell>Cód. Proveedor</StyledTableHeadCell>
              <StyledTableHeadCell>Largo (cm)</StyledTableHeadCell>
              <InventorySortableHeadCell
                label="Precio de venta"
                field="salePrice"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              {!hideTransferColumns && (
                <>
                  <InventorySortableHeadCell
                    label="Fecha Transfer."
                    field="firstAssignedAt"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <StyledTableHeadCell>Cód. Transferencia</StyledTableHeadCell>
                </>
              )}
              <InventorySortableHeadCell
                label="Estado Venta"
                field="availabilityStatus"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
              />
              {!hideLocationColumn && (
                <InventorySortableHeadCell
                  label="Sucursal"
                  field="locationName"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSortChange={onSortChange}
                />
              )}
              <StyledTableHeadCell>Acción</StyledTableHeadCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {items.length > 0 ? (
              items.map((item) => (
                <StyledTableRow
                  key={item.id}
                  hover
                  selected={false}
                  sx={tableRowTransparentSelectionSx}
                >
                  <StyledTableCell padding="checkbox">
                    <Tooltip
                      title={
                        !isSelectableForWarehouseTransfer(item)
                          ? item.status !== INV_STATUS_WAREHOUSE_LABEL
                            ? `No se puede seleccionar: solo piezas en almacén (estado: "${item.status}")`
                            : item.availabilityStatus === INV_AVAILABILITY_BLOCKED_LABEL
                              ? 'No se puede seleccionar: pieza bloqueada'
                              : `No se puede seleccionar: estado de venta "${item.availabilityStatus}"`
                          : 'Haz clic para seleccionar esta pieza'
                      }
                      placement="top"
                      arrow
                    >
                      <Box sx={{ position: 'relative' }}>
                        <Checkbox
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          disabled={!isSelectableForWarehouseTransfer(item)}
                          size="small"
                          sx={getCheckboxStyles(isSelectableForWarehouseTransfer(item))}
                        />
                      </Box>
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell tone="muted">{item.id}</StyledTableCell>
                  <StyledTableCell tone="muted">{item.name}</StyledTableCell>
                  <StyledTableCell tone="muted">{item.type}</StyledTableCell>
                  <StyledTableCell tone="muted">{formatDate(item.createdAt)}</StyledTableCell>
                  <StyledTableCell tone="muted" sx={{ minWidth: 108 }}>
                    {formatDaysInStock(item)}
                  </StyledTableCell>
                  <StyledTableCell tone="muted">{item.recordCode ?? '—'}</StyledTableCell>
                  <StyledTableCell>
                    {(() => {
                      const chip = mapWarehouseStatus(item.status);
                      return <StatusChip label={chip.label} size="small" color={chip.color} />;
                    })()}
                  </StyledTableCell>
                  <StyledTableCell tone="muted">{displayText(item.diameterSize)}</StyledTableCell>
                  <StyledTableCell tone="muted">{displayText(item.color)}</StyledTableCell>
                  <StyledTableCell tone="muted">{displayText(item.workshopCode)}</StyledTableCell>
                  <StyledTableCell tone="muted">{formatLengthCm(item.lengthMm)}</StyledTableCell>
                  <StyledTableCell
                    tone="muted"
                    align="right"
                    sx={{ minWidth: 108, ...pieceSalePriceTableCellSx }}
                  >
                    {formatPieceSalePrice(item.price)}
                  </StyledTableCell>
                  {!hideTransferColumns && (
                    <>
                      <StyledTableCell tone="muted">
                        {formatDate(getTransferDisplayAtIso(item.transfer, item.firstAssignedAt))}
                      </StyledTableCell>
                      <StyledTableCell tone="muted">
                        {item.transfer?.transferCode || '-'}
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>
                    {(() => {
                      const chip = mapInventoryAvailability(item.availabilityStatus);
                      return <StatusChip label={chip.label} size="small" color={chip.color} />;
                    })()}
                  </StyledTableCell>
                  {!hideLocationColumn && (
                    <StyledTableCell tone="muted">{item.locationName || '-'}</StyledTableCell>
                  )}
                  <StyledTableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Ver detalles" placement="top" arrow>
                        <ViewButton size="small" onClick={() => onViewDetails(item.id)}>
                          <VisibilityIcon />
                        </ViewButton>
                      </Tooltip>
                      {onEditItem &&
                        warehousePerms.canEdit &&
                        !isAvailabilityWrittenOff(item.availabilityStatus) && (
                          <Tooltip title="Editar pieza" placement="top" arrow>
                            <ActionButton size="small" onClick={() => onEditItem(item.id)}>
                              <EditIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        )}
                      {onReserveItem &&
                        isEligibleForWarehouseBlock(item.status) &&
                        item.availabilityStatus === 'Disponible' &&
                        warehousePerms.canReserve && (
                          <Tooltip
                            title="Bloquear pieza (impide transferencia)"
                            placement="top"
                            arrow
                          >
                            <ActionButton size="small" onClick={() => onReserveItem(item.id)}>
                              <LockIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        )}
                      {onReserveItem &&
                        isEligibleForWarehouseBlock(item.status) &&
                        item.availabilityStatus === INV_AVAILABILITY_BLOCKED_LABEL &&
                        warehousePerms.canUnreserve && (
                          <Tooltip title="Liberar bloqueo" placement="top" arrow>
                            <ActionButton
                              size="small"
                              colorVariant="error"
                              onClick={() => onReserveItem(item.id)}
                            >
                              <LockOpenIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        )}
                      {onPrintLabel && !isAvailabilityWrittenOff(item.availabilityStatus) && (
                        <Tooltip title="Imprimir etiqueta" placement="top" arrow>
                          <ViewButton size="small" onClick={() => onPrintLabel(item)}>
                            <ReceiptIcon fontSize="small" />
                          </ViewButton>
                        </Tooltip>
                      )}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={emptyStateColSpan} align="center">
                  <EmptyStateMessage message="No se encontraron piezas" minHeight={200} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </TableContainerStyled>
  );
};

export default WarehouseTable;
